// ---------------------------------------------------------------------------
// Felton Discovery -- Filter Constants
// ---------------------------------------------------------------------------
// All constant values for MoreFiltersPanel filters.
// Includes option arrays for all 23 filter dimensions.
// ---------------------------------------------------------------------------

import type {
  FilterOption,
  IndustryOption,
  EmotionOption,
  BrandCategoryOption,
  MoreFiltersState,
} from './filterTypes'

// Re-export from parent constants
export { AGE_LABELS, FOLLOWER_TIERS } from '../constants'

// ---------------------------------------------------------------------------
// Search Debounce
// ---------------------------------------------------------------------------

export const FILTER_SEARCH_DEBOUNCE_MS = 300

// ---------------------------------------------------------------------------
// Demographics Options
// ---------------------------------------------------------------------------

export const GENDER_OPTIONS: ReadonlyArray<FilterOption> = [
  { id: 'male', label: 'Male' },
  { id: 'female', label: 'Female' },
] as const

export const ETHNICITY_OPTIONS: ReadonlyArray<FilterOption> = [
  { id: 'white', label: 'White' },
  { id: 'black', label: 'Black' },
  { id: 'hispanic', label: 'Hispanic/Latino' },
  { id: 'asian', label: 'Asian' },
  { id: 'middle-eastern', label: 'Middle Eastern' },
  { id: 'native', label: 'Native American' },
  { id: 'mixed', label: 'Mixed/Other' },
] as const

export const COUNTRY_OPTIONS: ReadonlyArray<FilterOption> = [
  // Top 30 countries by social media usage
  { id: 'us', label: 'United States' },
  { id: 'br', label: 'Brazil' },
  { id: 'in', label: 'India' },
  { id: 'gb', label: 'United Kingdom' },
  { id: 'id', label: 'Indonesia' },
  { id: 'mx', label: 'Mexico' },
  { id: 'de', label: 'Germany' },
  { id: 'fr', label: 'France' },
  { id: 'it', label: 'Italy' },
  { id: 'es', label: 'Spain' },
  { id: 'tr', label: 'Turkey' },
  { id: 'ru', label: 'Russia' },
  { id: 'jp', label: 'Japan' },
  { id: 'ca', label: 'Canada' },
  { id: 'au', label: 'Australia' },
  { id: 'ar', label: 'Argentina' },
  { id: 'co', label: 'Colombia' },
  { id: 'th', label: 'Thailand' },
  { id: 'ph', label: 'Philippines' },
  { id: 'pl', label: 'Poland' },
  { id: 'nl', label: 'Netherlands' },
  { id: 'sa', label: 'Saudi Arabia' },
  { id: 'eg', label: 'Egypt' },
  { id: 'za', label: 'South Africa' },
  { id: 'vn', label: 'Vietnam' },
  { id: 'my', label: 'Malaysia' },
  { id: 'kr', label: 'South Korea' },
  { id: 'pe', label: 'Peru' },
  { id: 'ng', label: 'Nigeria' },
  { id: 'pt', label: 'Portugal' },
  { id: 'cl', label: 'Chile' },
  { id: 'gr', label: 'Greece' },
  { id: 'ro', label: 'Romania' },
  { id: 'be', label: 'Belgium' },
  { id: 'se', label: 'Sweden' },
] as const

export const INDUSTRY_OPTIONS: ReadonlyArray<IndustryOption> = [
  // Technology
  { id: 'tech-software', label: 'Software', sector: 'Technology' },
  { id: 'tech-hardware', label: 'Hardware', sector: 'Technology' },
  { id: 'tech-telecom', label: 'Telecommunications', sector: 'Technology' },

  // Finance
  { id: 'finance-banking', label: 'Banking', sector: 'Finance' },
  { id: 'finance-insurance', label: 'Insurance', sector: 'Finance' },
  { id: 'finance-investment', label: 'Investment', sector: 'Finance' },

  // Healthcare
  { id: 'health-pharma', label: 'Pharmaceuticals', sector: 'Healthcare' },
  { id: 'health-biotech', label: 'Biotechnology', sector: 'Healthcare' },
  { id: 'health-services', label: 'Health Services', sector: 'Healthcare' },

  // Retail
  { id: 'retail-ecommerce', label: 'E-commerce', sector: 'Retail' },
  { id: 'retail-fashion', label: 'Fashion', sector: 'Retail' },
  { id: 'retail-luxury', label: 'Luxury Goods', sector: 'Retail' },

  // Media
  { id: 'media-entertainment', label: 'Entertainment', sector: 'Media' },
  { id: 'media-publishing', label: 'Publishing', sector: 'Media' },
  { id: 'media-gaming', label: 'Gaming', sector: 'Media' },

  // Sports
  { id: 'sports-teams', label: 'Sports Teams', sector: 'Sports' },
  { id: 'sports-apparel', label: 'Sports Apparel', sector: 'Sports' },
  { id: 'sports-equipment', label: 'Sports Equipment', sector: 'Sports' },

  // Food & Beverage
  { id: 'food-restaurants', label: 'Restaurants', sector: 'Food & Beverage' },
  { id: 'food-beverage', label: 'Beverages', sector: 'Food & Beverage' },
  { id: 'food-cpg', label: 'Consumer Packaged Goods', sector: 'Food & Beverage' },

  // Other
  { id: 'auto-automotive', label: 'Automotive', sector: 'Automotive' },
  { id: 'energy-oil', label: 'Energy', sector: 'Energy' },
  { id: 'real-estate', label: 'Real Estate', sector: 'Real Estate' },
  { id: 'education', label: 'Education', sector: 'Education' },
] as const

// ---------------------------------------------------------------------------
// Psychographics Options
// ---------------------------------------------------------------------------

export const LIFESTYLE_OPTIONS: ReadonlyArray<FilterOption> = [
  { id: 'luxury', label: 'Luxury' },
  { id: 'wellness', label: 'Wellness' },
  { id: 'adventure', label: 'Adventure' },
  { id: 'urban', label: 'Urban' },
  { id: 'family', label: 'Family-Oriented' },
] as const

export const STATUS_CLASS_OPTIONS: ReadonlyArray<FilterOption> = [
  { id: 'elite', label: 'Elite' },
  { id: 'upper', label: 'Upper Class' },
  { id: 'middle', label: 'Middle Class' },
  { id: 'working', label: 'Working Class' },
  { id: 'emerging', label: 'Emerging Class' },
] as const

// ---------------------------------------------------------------------------
// Behaviour Options
// ---------------------------------------------------------------------------

export const EMOTION_OPTIONS: ReadonlyArray<EmotionOption> = [
  // Joy
  { id: 'joy', label: 'Joy', group: 'Positive' },
  { id: 'excitement', label: 'Excitement', group: 'Positive' },
  { id: 'love', label: 'Love', group: 'Positive' },
  { id: 'gratitude', label: 'Gratitude', group: 'Positive' },

  // Sadness
  { id: 'sadness', label: 'Sadness', group: 'Negative' },
  { id: 'grief', label: 'Grief', group: 'Negative' },
  { id: 'disappointment', label: 'Disappointment', group: 'Negative' },

  // Anger
  { id: 'anger', label: 'Anger', group: 'Negative' },
  { id: 'frustration', label: 'Frustration', group: 'Negative' },
  { id: 'disgust', label: 'Disgust', group: 'Negative' },

  // Fear
  { id: 'fear', label: 'Fear', group: 'Negative' },
  { id: 'anxiety', label: 'Anxiety', group: 'Negative' },
  { id: 'worry', label: 'Worry', group: 'Negative' },

  // Surprise
  { id: 'surprise', label: 'Surprise', group: 'Neutral' },
  { id: 'shock', label: 'Shock', group: 'Neutral' },

  // Neutral
  { id: 'calm', label: 'Calm', group: 'Neutral' },
  { id: 'curious', label: 'Curious', group: 'Neutral' },
  { id: 'thoughtful', label: 'Thoughtful', group: 'Neutral' },

  // Mixed
  { id: 'hope', label: 'Hope', group: 'Mixed' },
  { id: 'pride', label: 'Pride', group: 'Mixed' },
  { id: 'jealousy', label: 'Jealousy', group: 'Mixed' },
  { id: 'nostalgia', label: 'Nostalgia', group: 'Mixed' },
] as const

export const LANGUAGE_OPTIONS: ReadonlyArray<FilterOption> = [
  { id: 'en', label: 'English' },
  { id: 'es', label: 'Spanish' },
  { id: 'pt', label: 'Portuguese' },
  { id: 'fr', label: 'French' },
  { id: 'de', label: 'German' },
  { id: 'it', label: 'Italian' },
  { id: 'ru', label: 'Russian' },
  { id: 'ja', label: 'Japanese' },
  { id: 'ko', label: 'Korean' },
  { id: 'zh', label: 'Chinese' },
  { id: 'ar', label: 'Arabic' },
  { id: 'hi', label: 'Hindi' },
  { id: 'bn', label: 'Bengali' },
  { id: 'tr', label: 'Turkish' },
  { id: 'vi', label: 'Vietnamese' },
  { id: 'pl', label: 'Polish' },
  { id: 'th', label: 'Thai' },
  { id: 'nl', label: 'Dutch' },
  { id: 'id', label: 'Indonesian' },
  { id: 'ms', label: 'Malay' },
  { id: 'fa', label: 'Persian' },
  { id: 'uk', label: 'Ukrainian' },
  { id: 'ro', label: 'Romanian' },
  { id: 'sv', label: 'Swedish' },
  { id: 'cs', label: 'Czech' },
  { id: 'el', label: 'Greek' },
  { id: 'he', label: 'Hebrew' },
  { id: 'da', label: 'Danish' },
  { id: 'fi', label: 'Finnish' },
  { id: 'no', label: 'Norwegian' },
  { id: 'hu', label: 'Hungarian' },
  { id: 'sk', label: 'Slovak' },
] as const

// ---------------------------------------------------------------------------
// Content Options
// ---------------------------------------------------------------------------

export const CONTENT_CATEGORY_OPTIONS: ReadonlyArray<FilterOption> = [
  { id: 'sports', label: 'Sports' },
  { id: 'entertainment', label: 'Entertainment' },
  { id: 'lifestyle', label: 'Lifestyle' },
  { id: 'news', label: 'News' },
  { id: 'education', label: 'Education' },
  { id: 'comedy', label: 'Comedy' },
  { id: 'music', label: 'Music' },
  { id: 'gaming', label: 'Gaming' },
  { id: 'tech', label: 'Technology' },
] as const

export const MEDIA_TYPE_OPTIONS: ReadonlyArray<FilterOption> = [
  { id: 'video', label: 'Video' },
  { id: 'image', label: 'Image' },
  { id: 'carousel', label: 'Carousel' },
  { id: 'short', label: 'Short/Reel' },
  { id: 'live', label: 'Live' },
] as const

// ---------------------------------------------------------------------------
// Entity Options
// ---------------------------------------------------------------------------

export const BRAND_CATEGORY_OPTIONS: ReadonlyArray<BrandCategoryOption> = [
  {
    id: 'apparel',
    label: 'Apparel & Footwear',
    subcategories: [
      { id: 'apparel-sportswear', label: 'Sportswear' },
      { id: 'apparel-casual', label: 'Casual Wear' },
      { id: 'apparel-luxury', label: 'Luxury Fashion' },
      { id: 'apparel-streetwear', label: 'Streetwear' },
    ],
  },
  {
    id: 'sports-equipment',
    label: 'Sports Equipment',
    subcategories: [
      { id: 'equipment-soccer', label: 'Soccer Equipment' },
      { id: 'equipment-basketball', label: 'Basketball Equipment' },
      { id: 'equipment-tennis', label: 'Tennis Equipment' },
      { id: 'equipment-golf', label: 'Golf Equipment' },
    ],
  },
  {
    id: 'beverages',
    label: 'Beverages',
    subcategories: [
      { id: 'bev-soft-drinks', label: 'Soft Drinks' },
      { id: 'bev-energy', label: 'Energy Drinks' },
      { id: 'bev-water', label: 'Water' },
      { id: 'bev-alcohol', label: 'Alcoholic Beverages' },
    ],
  },
  {
    id: 'automotive',
    label: 'Automotive',
    subcategories: [
      { id: 'auto-luxury', label: 'Luxury Cars' },
      { id: 'auto-electric', label: 'Electric Vehicles' },
      { id: 'auto-mass', label: 'Mass Market' },
    ],
  },
  {
    id: 'technology',
    label: 'Technology',
    subcategories: [
      { id: 'tech-smartphones', label: 'Smartphones' },
      { id: 'tech-computers', label: 'Computers' },
      { id: 'tech-gaming', label: 'Gaming Consoles' },
      { id: 'tech-wearables', label: 'Wearables' },
    ],
  },
  {
    id: 'financial',
    label: 'Financial Services',
    subcategories: [
      { id: 'fin-banking', label: 'Banking' },
      { id: 'fin-insurance', label: 'Insurance' },
      { id: 'fin-crypto', label: 'Cryptocurrency' },
      { id: 'fin-payment', label: 'Payment Services' },
    ],
  },
  {
    id: 'telecom',
    label: 'Telecommunications',
    subcategories: [
      { id: 'telecom-mobile', label: 'Mobile Carriers' },
      { id: 'telecom-internet', label: 'Internet Providers' },
    ],
  },
  {
    id: 'food',
    label: 'Food & Restaurants',
    subcategories: [
      { id: 'food-fast', label: 'Fast Food' },
      { id: 'food-casual', label: 'Casual Dining' },
      { id: 'food-delivery', label: 'Food Delivery' },
      { id: 'food-snacks', label: 'Snacks & Confectionery' },
    ],
  },
  {
    id: 'health',
    label: 'Health & Wellness',
    subcategories: [
      { id: 'health-supplements', label: 'Supplements' },
      { id: 'health-fitness', label: 'Fitness' },
      { id: 'health-beauty', label: 'Beauty & Cosmetics' },
    ],
  },
  {
    id: 'media',
    label: 'Media & Entertainment',
    subcategories: [
      { id: 'media-streaming', label: 'Streaming Services' },
      { id: 'media-gaming', label: 'Video Games' },
      { id: 'media-music', label: 'Music Streaming' },
      { id: 'media-social', label: 'Social Media Platforms' },
    ],
  },
  {
    id: 'travel',
    label: 'Travel & Hospitality',
    subcategories: [
      { id: 'travel-airlines', label: 'Airlines' },
      { id: 'travel-hotels', label: 'Hotels' },
      { id: 'travel-booking', label: 'Travel Booking' },
    ],
  },
  {
    id: 'retail',
    label: 'Retail & E-commerce',
    subcategories: [
      { id: 'retail-online', label: 'Online Marketplaces' },
      { id: 'retail-department', label: 'Department Stores' },
      { id: 'retail-electronics', label: 'Electronics Retail' },
    ],
  },
  {
    id: 'education',
    label: 'Education',
    subcategories: [
      { id: 'edu-online', label: 'Online Learning' },
      { id: 'edu-language', label: 'Language Learning' },
      { id: 'edu-tutoring', label: 'Tutoring Services' },
    ],
  },
  {
    id: 'nonprofit',
    label: 'Nonprofit & Causes',
    subcategories: [
      { id: 'nonprofit-charity', label: 'Charities' },
      { id: 'nonprofit-environmental', label: 'Environmental Causes' },
      { id: 'nonprofit-social', label: 'Social Justice' },
    ],
  },
] as const

// ---------------------------------------------------------------------------
// Empty Filter State
// ---------------------------------------------------------------------------

export const EMPTY_FILTER_STATE: MoreFiltersState = Object.freeze({
  demographics: Object.freeze({
    gender: [],
    age: [],
    ethnicity: [],
    country: [],
    followerTier: [],
    industry: [],
  }),
  psychographics: Object.freeze({
    lifestyle: [],
    statusClass: [],
  }),
  behaviour: Object.freeze({
    emotions: [],
    sentiment: Object.freeze({ min: 0, max: 100 }),
    language: [],
  }),
  content: Object.freeze({
    categories: [],
    mediaType: [],
    paidOrganic: 'all',
  }),
  entities: Object.freeze({
    brands: [],
    brandCategory: [],
    hashtags: [],
  }),
  quality: Object.freeze({
    isPerson: 'all',
    verified: 'all',
    famous: 'all',
    spam: 'all',
    toxic: 'all',
  }),
})
